'use client';

import React, { useState, useMemo } from 'react';
import { useSearchParams } from 'next/navigation';
import { SlidersHorizontal, X } from 'lucide-react';
import { PRODUCTS, PRICE_LISTINGS, KIBBLE_TYPE_LABELS, type DogSize, type KibbleType,  } from '@/lib/mockData';
import FilterSidebar from './FilterSidebar';
import ProductCard from './ProductCard';
import SortBar from './SortBar';
import ResultsPagination from './ResultsPagination';

export type SortOption = 'best-price' | 'price-per-kg' | 'popularity' | 'most-retailers';

export interface Filters {
  brands: string[];
  dogSizes: DogSize[];
  kibbleTypes: KibbleType[];
  maxPrice: number;
  freeShippingOnly: boolean;
}

const ITEMS_PER_PAGE = 6;

export default function SearchResultsContent() {
  const searchParams = useSearchParams();
  const query = searchParams.get('q') || '';
  const initialSize = searchParams.get('size') as DogSize | null;

  const [sort, setSort] = useState<SortOption>(
    (searchParams.get('sort') === 'best-deal' ? 'best-price' : 'best-price') as SortOption
  );
  const [filters, setFilters] = useState<Filters>({
    brands: [],
    dogSizes: initialSize ? [initialSize] : [],
    kibbleTypes: [],
    maxPrice: 200,
    freeShippingOnly: false,
  });
  const [page, setPage] = useState(1);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Filter + sort products — Backend integration point: replace with API search endpoint
  const filteredProducts = useMemo(() => {
    let result = PRODUCTS.filter((p) => {
      if (query) {
        const q = query.toLowerCase();
        if (!p.name.toLowerCase().includes(q) && !p.brand.toLowerCase().includes(q)) return false;
      }
      if (filters.brands.length > 0 && !filters.brands.includes(p.brand)) return false;
      if (filters.dogSizes.length > 0 && !p.dogSizes.some((s) => filters.dogSizes.includes(s))) return false;
      if (filters.kibbleTypes.length > 0 && !filters.kibbleTypes.includes(p.kibbleType)) return false;

      // Check price filter
      const listings = PRICE_LISTINGS.filter(
        (l) => l.productId === p.id && l.stockStatus !== 'out-of-stock'
      );
      if (!listings.length) return false;
      const bestPrice = Math.min(...listings.map((l) => l.price));
      if (bestPrice > filters.maxPrice) return false;

      if (filters.freeShippingOnly) {
        const hasFreeShipping = listings.some((l) => l.shippingCost === null);
        if (!hasFreeShipping) return false;
      }

      return true;
    });

    // Sort
    result = [...result].sort((a, b) => {
      const aListings = PRICE_LISTINGS.filter((l) => l.productId === a.id && l.stockStatus !== 'out-of-stock');
      const bListings = PRICE_LISTINGS.filter((l) => l.productId === b.id && l.stockStatus !== 'out-of-stock');

      if (sort === 'best-price') {
        const aMin = aListings.length ? Math.min(...aListings.map((l) => l.price)) : 999;
        const bMin = bListings.length ? Math.min(...bListings.map((l) => l.price)) : 999;
        return aMin - bMin;
      }
      if (sort === 'price-per-kg') {
        const aMin = aListings.length ? Math.min(...aListings.map((l) => l.pricePerKg)) : 999;
        const bMin = bListings.length ? Math.min(...bListings.map((l) => l.pricePerKg)) : 999;
        return aMin - bMin;
      }
      if (sort === 'popularity') {
        return b.reviewCount - a.reviewCount;
      }
      if (sort === 'most-retailers') {
        return new Set(bListings.map((l) => l.retailerId)).size - new Set(aListings.map((l) => l.retailerId)).size;
      }
      return 0;
    });

    return result;
  }, [query, filters, sort]);

  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);
  const paginatedProducts = filteredProducts.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  function clearFilter(type: keyof Filters, value?: string) {
    setFilters((prev) => {
      if (type === 'brands') return { ...prev, brands: prev.brands.filter((b) => b !== value) };
      if (type === 'dogSizes') return { ...prev, dogSizes: prev.dogSizes.filter((s) => s !== value) };
      if (type === 'kibbleTypes') return { ...prev, kibbleTypes: prev.kibbleTypes.filter((k) => k !== value) };
      if (type === 'freeShippingOnly') return { ...prev, freeShippingOnly: false };
      return prev;
    });
    setPage(1);
  }

  const activeFilterCount =
    filters.brands.length +
    filters.dogSizes.length +
    filters.kibbleTypes.length +
    (filters.freeShippingOnly ? 1 : 0);

  return (
    <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-display text-2xl font-bold text-gray-900">
          {query ? (
            <>Résultats pour &ldquo;<span className="text-orange-600">{query}</span>&rdquo;</>
          ) : (
            'Toutes les croquettes'
          )}
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          {filteredProducts.length} produit{filteredProducts.length !== 1 ? 's' : ''} trouvé{filteredProducts.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Active filter chips */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap gap-2 mb-5">
          {filters.brands.map((b) => (
            <button
              key={`chip-brand-${b}`}
              onClick={() => clearFilter('brands', b)}
              className="inline-flex items-center gap-1.5 bg-orange-100 text-orange-700 text-xs font-semibold px-3 py-1.5 rounded-full hover:bg-orange-200 transition-colors"
            >
              {b}
              <X size={12} />
            </button>
          ))}
          {filters.dogSizes.map((s) => (
            <button
              key={`chip-size-${s}`}
              onClick={() => clearFilter('dogSizes', s)}
              className="inline-flex items-center gap-1.5 bg-teal-100 text-teal-700 text-xs font-semibold px-3 py-1.5 rounded-full hover:bg-teal-200 transition-colors"
            >
              Taille {s}
              <X size={12} />
            </button>
          ))}
          {filters.kibbleTypes.map((k) => (
            <button
              key={`chip-type-${k}`}
              onClick={() => clearFilter('kibbleTypes', k)}
              className="inline-flex items-center gap-1.5 bg-purple-100 text-purple-700 text-xs font-semibold px-3 py-1.5 rounded-full hover:bg-purple-200 transition-colors"
            >
              {KIBBLE_TYPE_LABELS[k]}
              <X size={12} />
            </button>
          ))}
          {filters.freeShippingOnly && (
            <button
              key="chip-free-shipping"
              onClick={() => clearFilter('freeShippingOnly')}
              className="inline-flex items-center gap-1.5 bg-green-100 text-green-700 text-xs font-semibold px-3 py-1.5 rounded-full hover:bg-green-200 transition-colors"
            >
              Livraison gratuite
              <X size={12} />
            </button>
          )}
          <button
            onClick={() => {
              setFilters({ brands: [], dogSizes: [], kibbleTypes: [], maxPrice: 200, freeShippingOnly: false });
              setPage(1);
            }}
            className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-600 text-xs font-semibold px-3 py-1.5 rounded-full hover:bg-gray-200 transition-colors"
          >
            Tout effacer
          </button>
        </div>
      )}

      {/* Sort bar */}
      <SortBar sort={sort} setSort={(s) => { setSort(s); setPage(1); }} resultCount={filteredProducts.length} />

      <div className="flex gap-6 mt-6">
        {/* Sidebar — desktop */}
        <aside className="hidden lg:block w-64 xl:w-72 shrink-0">
          <FilterSidebar filters={filters} setFilters={(f) => { setFilters(f); setPage(1); }} />
        </aside>

        {/* Mobile filter button */}
        <button
          onClick={() => setMobileFiltersOpen(true)}
          className="lg:hidden fixed bottom-6 right-6 z-40 flex items-center gap-2 bg-orange-500 text-white font-semibold px-4 py-3 rounded-2xl shadow-lg hover:bg-orange-600 transition-colors"
        >
          <SlidersHorizontal size={18} />
          Filtres
          {activeFilterCount > 0 && (
            <span className="bg-white text-orange-600 text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
        </button>

        {/* Mobile filter drawer */}
        {mobileFiltersOpen && (
          <div className="lg:hidden fixed inset-0 z-50 flex">
            <div
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
              onClick={() => setMobileFiltersOpen(false)}
            />
            <div className="relative ml-auto w-80 max-w-full bg-white h-full overflow-y-auto p-5 animate-slide-up">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display font-bold text-lg">Filtres</h2>
                <button onClick={() => setMobileFiltersOpen(false)} className="p-2 rounded-lg hover:bg-gray-100">
                  <X size={20} />
                </button>
              </div>
              <FilterSidebar filters={filters} setFilters={(f) => { setFilters(f); setPage(1); }} />
            </div>
          </div>
        )}

        {/* Results grid */}
        <div className="flex-1 min-w-0">
          {paginatedProducts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mb-4">
                <SlidersHorizontal size={28} className="text-orange-400" />
              </div>
              <h3 className="font-display font-bold text-gray-900 text-lg mb-2">Aucun produit trouvé</h3>
              <p className="text-gray-500 text-sm max-w-xs">
                Aucune croquette ne correspond à vos critères. Essayez d'élargir vos filtres ou de modifier votre recherche.
              </p>
              <button
                onClick={() => {
                  setFilters({ brands: [], dogSizes: [], kibbleTypes: [], maxPrice: 200, freeShippingOnly: false });
                  setPage(1);
                }}
                className="mt-5 btn-primary"
              >
                Réinitialiser les filtres
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 gap-5">
                {paginatedProducts.map((product) => (
                  <ProductCard key={`result-${product.id}`} product={product} />
                ))}
              </div>
              <ResultsPagination
                page={page}
                totalPages={totalPages}
                totalItems={filteredProducts.length}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={setPage}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}